<template>
  <nav class="accessible-pagination" :aria-label="t('common.pagination')">
    <ul class="accessible-pagination__list q-pagination row no-wrap items-center">
      <li
        v-for="item in items"
        :key="item.key"
        class="accessible-pagination__item"
      >
        <!-- Page number / ellipsis -->
        <q-btn
          v-if="item.kind === 'page' || item.kind === 'ellipsis'"
          :flat="item.active ? flat : true"
          :outline="outline"
          :color="item.active ? activeColor || color : color"
          :text-color="item.active ? activeTextColor || textColor : undefined"
          :label="item.kind === 'ellipsis' ? '…' : String(item.page)"
          :size="size"
          :ripple="item.kind !== 'ellipsis'"
          :style="pageStyle"
          :disable="disable"
          :aria-label="`${t('common.page')} ${item.page}`"
          :aria-current="item.active ? 'page' : undefined"
          @click="select(item.page)"
        />
        <!-- Previous / next direction links -->
        <q-btn
          v-else
          flat
          :outline="outline"
          :color="color"
          :size="size"
          :icon="item.kind === 'prev' ? iconPrev : iconNext"
          :disable="item.disable"
          :aria-label="
            item.kind === 'prev'
              ? t('common.previous_page')
              : t('common.next_page')
          "
          @click="select(item.page)"
        />
      </li>
    </ul>
  </nav>
</template>

<script>
import { computed } from "vue";
import { useI18n } from "vue-i18n";

export default {
  name: "AccessiblePagination",
  props: {
    modelValue: { type: Number, required: true },
    min: { type: Number, default: 1 },
    max: { type: Number, required: true },
    maxPages: { type: Number, default: 0 },
    color: { type: String, default: "primary" },
    textColor: { type: String, default: undefined },
    activeColor: { type: String, default: undefined },
    activeTextColor: { type: String, default: undefined },
    size: { type: String, default: undefined },
    flat: { type: Boolean, default: false },
    outline: { type: Boolean, default: false },
    disable: { type: Boolean, default: false },
    directionLinks: { type: Boolean, default: false },
    ellipses: { type: Boolean, default: true },
    boundaryNumbers: { type: Boolean, default: true },
    iconPrev: { type: String, default: "keyboard_arrow_left" },
    iconNext: { type: String, default: "keyboard_arrow_right" },
  },
  emits: ["update:modelValue"],
  setup(props, { emit }) {
    const { t } = useI18n();

    const pageStyle = computed(() => ({
      minWidth: `${Math.max(2, String(props.max).length)}em`,
    }));

    const items = computed(() => {
      const result = [];
      const {
        min,
        max,
        modelValue: model,
        ellipses,
        directionLinks,
        boundaryNumbers,
      } = props;

      if (directionLinks) {
        result.push({
          kind: "prev",
          page: model - 1,
          disable: props.disable || model <= min,
          key: "prev",
        });
      }

      let maxPages = Math.max(
        props.maxPages,
        1 + (ellipses ? 2 : 0) + (boundaryNumbers ? 2 : 0),
      );
      let pgFrom = min;
      let pgTo = max;
      let ellipsesStart = false;
      let ellipsesEnd = false;
      let boundaryStart = false;
      let boundaryEnd = false;

      if (props.maxPages && maxPages < max - min + 1) {
        maxPages = 1 + Math.floor(maxPages / 2) * 2;
        pgFrom = Math.max(
          min,
          Math.min(max - maxPages + 1, model - Math.floor(maxPages / 2)),
        );
        pgTo = Math.min(max, pgFrom + maxPages - 1);
        if (boundaryNumbers) {
          boundaryStart = true;
          pgFrom += 1;
        }
        if (ellipses && pgFrom > min + (boundaryNumbers ? 1 : 0)) {
          ellipsesStart = true;
          pgFrom += 1;
        }
        if (boundaryNumbers) {
          boundaryEnd = true;
          pgTo -= 1;
        }
        if (ellipses && pgTo < max - (boundaryNumbers ? 1 : 0)) {
          ellipsesEnd = true;
          pgTo -= 1;
        }
      }

      if (boundaryStart) {
        result.push({
          kind: "page",
          page: min,
          active: min === model,
          key: "boundary-start",
        });
      }
      if (ellipsesStart) {
        result.push({
          kind: "ellipsis",
          page: pgFrom - 1,
          key: "ellipsis-start",
        });
      }
      for (let i = pgFrom; i <= pgTo; i++) {
        result.push({
          kind: "page",
          page: i,
          active: i === model,
          key: `page-${i}`,
        });
      }
      if (ellipsesEnd) {
        result.push({ kind: "ellipsis", page: pgTo + 1, key: "ellipsis-end" });
      }
      if (boundaryEnd) {
        result.push({
          kind: "page",
          page: max,
          active: max === model,
          key: "boundary-end",
        });
      }
      if (directionLinks) {
        result.push({
          kind: "next",
          page: model + 1,
          disable: props.disable || model >= max,
          key: "next",
        });
      }

      return result;
    });

    const select = (page) => {
      if (props.disable) return;
      const value = Math.min(
        Math.max(parseInt(page, 10), props.min),
        props.max,
      );
      if (Number.isNaN(value) || value === props.modelValue) return;
      emit("update:modelValue", value);
    };

    return { t, items, pageStyle, select };
  },
};
</script>

<style scoped>
.accessible-pagination__list {
  list-style: none;
  margin: 0;
  padding: 0;
}
</style>
